import { Campaign, Collab, getEarliestSchedulableTime, visitDeadline } from '@foodfluence/shared';

const ymdIn = (d: Date, timezone: string) => new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(d);

/**
 * Copy for "no bookable slots" on a go-live collab/campaign, or null when the existing
 * "all slots booked" / "no times" copy applies. Only when go-live is why:
 * - specific dates (collab's override the campaign's): every date still ahead falls after the last
 *   visit day, so no visit leaves time for the draft review. The brand has to fix it: no extension CTA.
 * - open hours: the earliest bookable visit (now + the campaign's advance notice) is already past
 *   the last day a visit can be on (draft collabs: before go-live start minus review time; post
 *   collabs: go-live end). Slots full while an earlier visit is still possible is capacity, and
 *   keeps the capacity copy.
 */
export const getPostByNoSlotsMessage = (
  collab: Collab | null | undefined,
  campaign: Campaign | null | undefined,
  hasNoSlots: boolean,
  timezone: string,
  now: Date = new Date()
): string | null => {
  if (!hasNoSlots) return null;
  const deadline = visitDeadline(collab, campaign, timezone);
  if (!deadline) return null;
  // Collab times override the campaign's, as getCollabAvailableTimes (not imported: it pulls in Firebase).
  const collabDates = Object.keys(collab?.availableTimes ?? {});
  const dates = collabDates.length > 0 ? collabDates : Object.keys(campaign?.availableTimes ?? {});
  if (dates.length > 0) {
    const today = ymdIn(now, timezone);
    const last = ymdIn(deadline, timezone);
    const ahead = dates.filter((d) => d >= today);
    return ahead.length > 0 && ahead.every((d) => d > last)
      ? "The brand's visit dates are too late for this campaign's go-live. Reach out to support and we'll sort it out with the brand."
      : null;
  }
  if (getEarliestSchedulableTime(campaign, now) <= deadline) return null;
  const date = deadline.toLocaleDateString('en-US', { timeZone: timezone, month: 'short', day: 'numeric' });
  return `No visit times left by ${date}, the last day to visit for this campaign's go-live. Request an extension so the brand can give you more time.`;
};
